import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import toast from "react-hot-toast";
import Sidebar, { clearSession } from "../components/Sidebar";
import { applyBillingEnumText } from "../utils/enums";
import {
  ArrowLeft,
  Building2,
  Calendar,
  CheckCircle2,
  Clock,
  CreditCard,
  Eye,
  FileText,
  Home,
  Phone,
  User,
  Users,
  AlertTriangle,
  Bell,
  RefreshCw,
  Search,
  DollarSign,
  Plus,
  Trash2,
  X,
  Check
} from "lucide-react";

const PROD_API_BASE = "https://zangon-web-api.azurewebsites.net";
const API_BASE = PROD_API_BASE;
const SESSION_KEY = "zangon_session";

const HOUSE_TYPE_IMAGES = {
  "1 Bedroom Bungalow": "/assets/media/zangon/1bedroombungalo.jpg",
  "1 Bedrooms Bungalow": "/assets/media/zangon/1bedroombungalo.jpg",
  "2 Bedroom Bungalow": "/assets/media/zangon/2bedroom.jpg",
  "2 Bedrooms Bungalow": "/assets/media/zangon/2bedroom.jpg",
  "3 Bedrooms Bungalow": "/assets/media/zangon/3bedroomsbungalo.jpg",
  "3 Bedroom Bungalow": "/assets/media/zangon/3bedroomsbungalo.jpg",
  "4 Bedrooms Duplex": "/assets/media/zangon/4bd.jpg",
  "4 Bedroom Bungalow": "/assets/media/zangon/4bedroombungalo.jpg",
  "4 Bedrooms Bungalow": "/assets/media/zangon/4bedroombungalo.jpg",
  "5 Bedrooms Terrace": "/assets/media/zangon/5-light.jpg",
  "6 Bedroom Bungalow": "/assets/media/zangon/6bedroombungalow.jpg",
  "6 Bedrooms Bungalow": "/assets/media/zangon/6bedroombungalow.jpg",
  "6 Bedroom Terrace Duplex": "/assets/media/zangon/6bedroomterraceduple.jpg",
  "6 Bedrooms Terrace Duplex": "/assets/media/zangon/6bedroomterraceduple.jpg",
};

function getHouseImage(typeName) {
  if (!typeName) return "/assets/media/zangon/3bedroomsbungalo.jpg";
  const norm = typeName.toLowerCase().replace(/[^a-z0-9]/g, "").replace(/bedrooms?/g, "bedroom");
  if (norm.includes("1bedroom") || norm.includes("1bd")) {
    return "/assets/media/zangon/1bedroombungalo.jpg";
  }
  if (norm.includes("2bedroom") || norm.includes("2bd")) {
    return "/assets/media/zangon/2bedroom.jpg";
  }
  if (norm.includes("3bedroombungalow") || norm.includes("3bedroombungalo") || norm.includes("3bd")) {
    return "/assets/media/zangon/3bedroomsbungalo.jpg";
  }
  if (norm.includes("4bedroombungalow") || norm.includes("4bedroombungalo")) {
    return "/assets/media/zangon/4bedroombungalo.jpg";
  }
  if (norm.includes("4bedroomduplex") || norm.includes("4bd")) {
    return "/assets/media/zangon/4bd.jpg";
  }
  if (norm.includes("5bedroom")) {
    const isDark = document.documentElement.getAttribute("data-bs-theme") === "dark" ||
      localStorage.getItem("kt_theme_mode") === "dark";
    return isDark ? "/assets/media/zangon/5-dark.jpg" : "/assets/media/zangon/5-light.jpg";
  }
  if (norm.includes("6bedroombungalow")) {
    return "/assets/media/zangon/6bedroombungalow.jpg";
  }
  if (norm.includes("6bedroomterraceduplex") || norm.includes("6bedroomterraceduple") || (norm.includes("6bedroom") && norm.includes("terrace"))) {
    return "/assets/media/zangon/6bedroomterraceduple.jpg";
  }
  return HOUSE_TYPE_IMAGES[typeName] || "/assets/media/zangon/3bedroomsbungalo.jpg";
}

function PagePreloader({ visible }) {
  return (
    <div
      className={`page-preloader-overlay ${visible ? "is-visible" : ""}`}
      aria-hidden={!visible}
    >
      <div className="logo-box">
        <svg className="house-icon" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
          <path d="M 34 60 L 34 80 L 18 80 L 18 50 L 34 34 L 50 50 L 50 85" fill="none" stroke="#FFFFFF" strokeWidth="10" strokeLinecap="butt" strokeLinejoin="miter" />
          <path d="M 45.31 22.69 L 66 43.37 L 66 85" fill="none" stroke="#BBEE6B" strokeWidth="10" strokeLinecap="butt" strokeLinejoin="miter" />
          <path d="M 56.63 11.37 L 82 36.75 L 82 85" fill="none" stroke="#FFD75B" strokeWidth="10" strokeLinecap="butt" strokeLinejoin="miter" />
        </svg>
      </div>
    </div>
  );
}

function safeJsonParse(text) {
  try { return applyBillingEnumText(JSON.parse(text)); } catch { return null; }
}

function getSession() {
  try { return JSON.parse(localStorage.getItem(SESSION_KEY) || "null"); } catch { return null; }
}

function getAccessToken() { return getSession()?.accessToken || ""; }
function getRefreshToken() { return getSession()?.refreshToken || ""; }

async function fetchWithTimeout(url, options = {}, ms = 60000) {
  const ctrl = new AbortController();
  const id = setTimeout(() => ctrl.abort(), ms);
  try {
    const res = await fetch(url, { ...options, signal: ctrl.signal });
    return res;
  } catch (err) {
    if (err?.name === "AbortError") throw new Error("Request timed out.");
    throw err;
  } finally { clearTimeout(id); }
}

async function refreshAccessToken() {
  const refreshToken = getRefreshToken();
  if (!refreshToken) throw new Error("No refresh token.");
  const res = await fetchWithTimeout(`${API_BASE}/auth/refresh`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ refreshToken }),
  });
  const text = await res.text();
  const json = text ? safeJsonParse(text) : null;
  if (!res.ok) throw new Error(json?.message || "Token refresh failed.");
  const prev = getSession() || {};
  const next = { ...prev, ...(json?.data ?? json ?? {}) };
  localStorage.setItem(SESSION_KEY, JSON.stringify(next));
  localStorage.setItem("auth_token", next?.accessToken || "");
  return next;
}

async function authFetch(url, options = {}) {
  let token = getAccessToken();
  const headers = {
    Accept: "application/json",
    Authorization: `Bearer ${token}`,
    ...(options.headers || {}),
  };
  if (options.body && typeof options.body === "string" && !headers["Content-Type"]) {
    headers["Content-Type"] = "application/json";
  }
  let res = await fetchWithTimeout(url, { ...options, headers });
  if (res.status === 401) {
    try {
      const renewed = await refreshAccessToken();
      token = renewed.accessToken;
      headers.Authorization = `Bearer ${token}`;
      res = await fetchWithTimeout(url, { ...options, headers });
    } catch {
      clearSession();
      window.location.href = "/sign-in";
      throw new Error("Session expired. Please sign in again.");
    }
  }
  return res;
}

function parseArrayData(json) {
  if (Array.isArray(json)) return json;
  if (Array.isArray(json?.data)) return json.data;
  if (Array.isArray(json?.data?.data)) return json.data.data;
  if (Array.isArray(json?.items)) return json.items;
  return [];
}

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return "—";
    return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
  } catch { return "—"; }
}

function getInitials(firstName = "", lastName = "") {
  const f = firstName.trim()[0] || "";
  const l = lastName.trim()[0] || "";
  return (f + l).toUpperCase() || "T";
}

function getDueMeta(today, expiryISO) {
  if (!expiryISO) return null;
  const exp = new Date(expiryISO);
  if (isNaN(exp.getTime())) return null;
  const diffDays = Math.ceil((exp - today) / (1000 * 60 * 60 * 24));
  if (diffDays < 0) {
    const absDays = Math.abs(diffDays);
    return {
      badgeClass: "badge-light-danger",
      badgeText: "Overdue",
      days: `${absDays} day${absDays === 1 ? "" : "s"} ago`,
      expiryClass: "text-danger"
    };
  }
  if (diffDays <= 30) {
    return {
      badgeClass: "badge-light-warning",
      badgeText: "Due Soon",
      days: `In ${diffDays} day${diffDays === 1 ? "" : "s"}`,
      expiryClass: "text-warning"
    };
  }
  return {
    badgeClass: "badge-light-success",
    badgeText: "Active",
    days: `In ${diffDays} days`,
    expiryClass: "text-success"
  };
}

export default function RentDetails() {
  const { tenantId } = useParams();
  const navigate = useNavigate();
  const session = getSession();
  const estateId = session?.estateId || "";

  const [loading, setLoading] = useState(true);
  const [tenant, setTenant] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [payments, setPayments] = useState([]);
  const [activeTab, setActiveTab] = useState("invoices");
  const [searchQuery, setSearchQuery] = useState("");

  // Update Rent Modal States
  const [showUpdateRentModal, setShowUpdateRentModal] = useState(false);
  const [updatingRent, setUpdatingRent] = useState(false);
  const [newRentAmount, setNewRentAmount] = useState("");
  const [extraCharges, setExtraCharges] = useState([]);

  const today = useMemo(() => new Date(), []);

  const fetchData = useCallback(async () => {
    if (!tenantId || !estateId) return;
    setLoading(true);
    try {
      // 1. Fetch tenant detail
      // GET /estates/{id}/tenants/{tenantId} isn't a read endpoint (405); /details is.
      const tenantRes = await authFetch(`${API_BASE}/estates/${estateId}/tenants/${tenantId}/details`).catch(() => null);
      let tenantData = null;
      if (tenantRes && tenantRes.ok) {
        const json = safeJsonParse(await tenantRes.text());
        tenantData = json?.data || json;
      }

      // 2. Fetch upcoming rent for building info
      const upcomingRes = await authFetch(`${API_BASE}/estates/${estateId}/tenants/upcoming-rent?daysAhead=365&pageIndex=0&pageSize=100`).catch(() => null);
      let upcomingMatch = null;
      if (upcomingRes && upcomingRes.ok) {
        const json = safeJsonParse(await upcomingRes.text());
        const items = json?.data?.data || [];
        upcomingMatch = items.find(item => item.tenantId === tenantId);
      }

      // 3. Fetch rent invoices & payments
      const [invoicesRes, paymentsRes] = await Promise.all([
        authFetch(`${API_BASE}/estates/${estateId}/tenants/${tenantId}/rent-invoices`).catch(() => null),
        authFetch(`${API_BASE}/estates/${estateId}/tenants/${tenantId}/payments`).catch(() => null)
      ]);

      const invoicesJson = invoicesRes && invoicesRes.ok ? safeJsonParse(await invoicesRes.text()) : null;
      const paymentsJson = paymentsRes && paymentsRes.ok ? safeJsonParse(await paymentsRes.text()) : null;

      let invList = parseArrayData(invoicesJson);
      let payList = parseArrayData(paymentsJson);

      invList.sort((a, b) => new Date(b.issuedAt || b.createdDate || b.dueDate || 0) - new Date(a.issuedAt || a.createdDate || a.dueDate || 0));
      payList.sort((a, b) => new Date(b.paidAt || b.createdDate || 0) - new Date(a.paidAt || a.createdDate || 0));

      setInvoices(invList);
      setPayments(payList);

      const rentBuilding = upcomingMatch?.upcomingRentBuildings?.[0] || tenantData?.currentBuildings?.[0] || tenantData?.assignedBuildings?.[0] || tenantData?.assignedBuilding || {};
      const title = tenantData?.title || upcomingMatch?.title || "";
      const firstName = tenantData?.firstName || upcomingMatch?.firstName || "";
      const lastName = tenantData?.lastName || upcomingMatch?.lastName || "";
      const typeName = rentBuilding?.typeName || rentBuilding?.propertyTypeName || rentBuilding?.buildingTypeName || tenantData?.buildingType || "—";
      const buildingImageUrl = rentBuilding?.buildingImageUrl || rentBuilding?.imageUrl || tenantData?.imageUrl || getHouseImage(typeName);

      const rentComp = rentBuilding?.components?.find(c =>
        (c?.type || "").toLowerCase() === "rent" ||
        (c?.codeName || "").toLowerCase() === "rent" ||
        (c?.name || "").toLowerCase() === "rent"
      );
      const basicRentAmount = rentComp?.amount !== undefined && rentComp?.amount !== null
        ? rentComp.amount
        : (rentBuilding?.rentAmount || rentBuilding?.totalAmount || upcomingMatch?.totalAmountDue || 0);

      const basicRentFormatted = basicRentAmount !== undefined && basicRentAmount !== null && !isNaN(Number(basicRentAmount)) && Number(basicRentAmount) > 0
        ? `₦${Number(basicRentAmount).toLocaleString()}`
        : "—";

      const expiryISO = rentBuilding?.dueDate ? rentBuilding.dueDate.split("T")[0] : null;
      const buildingId = rentBuilding?.buildingId || rentBuilding?.id || tenantData?.buildingId || "";

      setTenant({
        id: tenantId,
        buildingId,
        title,
        firstName,
        lastName,
        name: `${title ? title + " " : ""}${firstName} ${lastName}`.trim() || "Tenant",
        email: tenantData?.email || upcomingMatch?.email || "—",
        phone: tenantData?.phoneNumber || upcomingMatch?.phoneNumber || "—",
        occupation: tenantData?.occupation || upcomingMatch?.occupation || "—",
        isCorporate: tenantData?.isCorporateTenant || upcomingMatch?.isCorporateTenant || false,
        houseNumber: rentBuilding?.buildingNumber || "—",
        phaseName: rentBuilding?.phaseName || "—",
        typeName,
        buildingImageUrl,
        rawBasicRent: basicRentAmount,
        basicRentFormatted,
        expiryISO,
        expiryLabel: expiryISO ? fmtDate(expiryISO) : "—",
        imageUrl: tenantData?.imageUrl || upcomingMatch?.imageUrl || null,
        initials: getInitials(firstName, lastName),
      });

      setNewRentAmount(basicRentAmount ? String(basicRentAmount) : "");
      if (buildingId) {
        const stored = localStorage.getItem(`zangon_building_charges_${buildingId}`);
        setExtraCharges(stored ? safeJsonParse(stored) || [] : []);
      }
    } catch (err) {
      console.error("Fetch Rent Details error:", err);
      toast.error("Failed to load house rent details.");
    } finally {
      setLoading(false);
    }
  }, [estateId, tenantId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const dueMeta = useMemo(() => {
    if (!tenant?.expiryISO) return { badgeClass: "badge-light-success", badgeText: "Active", days: "In 30 days", expiryClass: "text-success" };
    return getDueMeta(today, tenant.expiryISO);
  }, [tenant, today]);

  const filteredInvoices = useMemo(() => {
    if (!searchQuery.trim()) return invoices;
    const q = searchQuery.toLowerCase();
    return invoices.filter(inv =>
      (inv.id || "").toLowerCase().includes(q) ||
      (inv.status || "").toLowerCase().includes(q) ||
      (inv.code || "").toLowerCase().includes(q) ||
      String(inv.totalAmount || "").includes(q)
    );
  }, [invoices, searchQuery]);

  const filteredPayments = useMemo(() => {
    if (!searchQuery.trim()) return payments;
    const q = searchQuery.toLowerCase();
    return payments.filter(pay =>
      (pay.reference || pay.id || "").toLowerCase().includes(q) ||
      (pay.paymentMethod || "").toLowerCase().includes(q) ||
      (pay.status || "").toLowerCase().includes(q) ||
      String(pay.amount || "").includes(q)
    );
  }, [payments, searchQuery]);

  const handleSendReminder = () => {
    toast.success(`Rent reminder sent to ${tenant?.name || "tenant"}.`);
  };

  const handleUpdateRentSubmit = async (e) => {
    e.preventDefault();
    if (!newRentAmount || isNaN(Number(newRentAmount)) || Number(newRentAmount) <= 0) {
      return toast.error("Enter a valid rent amount.");
    }

    setUpdatingRent(true);
    try {
      if (tenant?.buildingId) {
        const res = await authFetch(
          `${API_BASE}/estates/${estateId}/buildings/${tenant.buildingId}/update-rent`,
          {
            method: "PUT",
            body: JSON.stringify({
              estateId,
              buildingId: tenant.buildingId,
              rentAmount: Number(newRentAmount)
            })
          }
        );
        const json = safeJsonParse(await res.text());
        if (!res.ok) throw new Error(json?.message || "Failed to update rent amount.");
        localStorage.setItem(`zangon_building_charges_${tenant.buildingId}`, JSON.stringify(extraCharges));
      }

      toast.success("Rent updated successfully!");
      setShowUpdateRentModal(false);
      fetchData();
    } catch (err) {
      toast.error(err.message || "Failed to update rent.");
    } finally {
      setUpdatingRent(false);
    }
  };

  return (
    <>
      <PagePreloader visible={loading} />
      <div className="d-flex flex-column flex-root app-root" id="kt_app_root">
        <div className="app-page flex-column flex-column-fluid" id="kt_app_page">
          <div className="app-wrapper flex-column flex-row-fluid" id="kt_app_wrapper">
            <Sidebar collapsible />

          <div className="app-main flex-column flex-row-fluid" id="kt_app_main">
            <div className="d-flex flex-column flex-column-fluid">

              {/* Toolbar Header */}
              <div id="kt_app_toolbar" className="app-toolbar py-4 py-lg-6 border-bottom bg-white">
                <div id="kt_app_toolbar_container" className="app-container container-fluid d-flex flex-stack">
                  <div className="d-flex align-items-center gap-3">
                    <button
                      type="button"
                      className="btn btn-icon btn-light btn-sm"
                      onClick={() => navigate(-1)}
                      title="Back"
                    >
                      <ArrowLeft className="w-4 h-4 text-gray-700" />
                    </button>
                    <div>
                      <h1 className="page-heading d-flex text-gray-900 fw-bold fs-3 flex-column justify-content-center my-0">
                        House Rent & Payment History
                      </h1>
                      <ul className="breadcrumb breadcrumb-separatorless fw-semibold fs-7 my-0 pt-1">
                        <li className="breadcrumb-item text-muted">
                          <Link to="/dashboard" className="text-muted text-hover-primary">Dashboard</Link>
                        </li>
                        <li className="breadcrumb-item"><span className="bullet bg-gray-300 w-5px h-2px"></span></li>
                        <li className="breadcrumb-item text-muted">
                          <Link to="/rent-management" className="text-muted text-hover-primary">Rent Management</Link>
                        </li>
                        <li className="breadcrumb-item"><span className="bullet bg-gray-300 w-5px h-2px"></span></li>
                        <li className="breadcrumb-item text-gray-900">Rent Details</li>
                      </ul>
                    </div>
                  </div>

                  <div className="d-flex align-items-center gap-3">
                    <button
                      type="button"
                      className="btn btn-light-primary btn-sm d-flex align-items-center gap-2"
                      onClick={fetchData}
                    >
                      <RefreshCw className="w-3.5 h-3.5" />
                      Refresh
                    </button>
                  </div>
                </div>
              </div>

              {/* Main Content */}
              <div id="kt_app_content" className="app-content flex-column-fluid p-6 p-lg-8">
                <div id="kt_app_content_container" className="app-container container-fluid">

                  {loading ? (
                    <div className="card p-10 text-center">
                      <span className="spinner-border spinner-border-sm text-success me-2"></span>Loading house rent details...
                    </div>
                  ) : !tenant ? (
                    <div className="card p-10 text-center text-muted">
                      <AlertTriangle className="w-10 h-10 text-warning mx-auto mb-3" />
                      <h4 className="fw-bold text-gray-900">Tenant / House record not found</h4>
                      <p className="fs-7 text-gray-500">The specified rent record could not be loaded.</p>
                      <button className="btn btn-primary btn-sm mx-auto" onClick={() => navigate("/dashboard")}>
                        Return to Dashboard
                      </button>
                    </div>
                  ) : (
                    <>
                      {/* Top Gradient Profile Card */}
                      <div
                        className="card card-flush mb-8 border-0 overflow-hidden"
                        style={{
                          background: "linear-gradient(135deg, rgb(6, 95, 70) 0%, rgb(15, 157, 88) 45%, rgb(18, 183, 106) 100%)",
                          borderRadius: "16px"
                        }}
                      >
                        <div className="card-body p-8">
                          <div className="d-flex flex-column flex-md-row align-items-center gap-6">
                            <div className="position-relative">
                              <div className="symbol symbol-100px symbol-circle border border-4 border-white overflow-hidden">
                                {tenant.imageUrl ? (
                                  <img alt={tenant.name} className="rounded-circle object-fit-cover w-100 h-100" src={tenant.imageUrl} />
                                ) : (
                                  <span className="symbol-label bg-white text-success fw-bold fs-1">
                                    {tenant.initials}
                                  </span>
                                )}
                              </div>
                            </div>
                            <div className="text-center text-md-start text-white flex-grow-1">
                              <h2 className="fs-1 fw-bold text-white mb-1">{tenant.name}</h2>
                              <div className="d-flex flex-wrap align-items-center justify-content-center justify-content-md-start gap-4 fs-7 text-white text-opacity-75">
                                <span><FileText className="w-4 h-4 text-white me-1 inline-block" />{tenant.email}</span>
                                <span>•</span>
                                <span><Phone className="w-4 h-4 text-white me-1 inline-block" />{tenant.phone}</span>
                                <span>•</span>
                                <span><Home className="w-4 h-4 text-white me-1 inline-block" />{tenant.phaseName} - Unit {tenant.houseNumber}</span>
                                <span>•</span>
                                <span className="badge bg-white bg-opacity-25 text-white border border-white border-opacity-25 fw-bold fs-8">
                                  {tenant.isCorporate ? "Corporate Tenant" : "Individual Tenant"}
                                </span>
                              </div>
                            </div>
                            <div className="d-flex gap-3 flex-wrap">
                              <button
                                type="button"
                                className="btn btn-light-success bg-white text-success fw-bold px-5 d-flex align-items-center gap-2"
                                onClick={() => setShowUpdateRentModal(true)}
                              >
                                <DollarSign className="w-4 h-4 text-success" />
                                Update Rent
                              </button>
                              <button
                                type="button"
                                className="btn btn-outline btn-outline-white text-white fw-bold px-5 d-flex align-items-center gap-2"
                                onClick={handleSendReminder}
                              >
                                <Bell className="w-4 h-4 text-white" />
                                Send Reminder
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Property & Payer Summaries Grid */}
                      <div className="row g-6 mb-8">

                        {/* Property Details */}
                        <div className="col-12 col-xl-6">
                          <div className="card card-flush h-100 border-0 bg-white" style={{ borderRadius: "16px" }}>
                            <div className="card-header border-bottom border-gray-200 py-4 px-6">
                              <h3 className="card-title fw-bold text-gray-900 fs-5 mb-0 d-flex align-items-center gap-2">
                                <Home className="w-4 h-4 text-success" />
                                Property & House Details
                              </h3>
                              <span className="badge badge-light-primary fw-bold fs-7">{tenant.phaseName}</span>
                            </div>

                            <div className="card-body p-6">
                              <div className="d-flex flex-column flex-sm-row gap-5 mb-6">
                                <div className="position-relative rounded-3 bg-light overflow-hidden flex-shrink-0" style={{ width: "160px", height: "120px" }}>
                                  <img
                                    src={tenant.buildingImageUrl}
                                    alt={tenant.typeName}
                                    style={{ width: "100%", height: "100%", objectFit: "cover" }}
                                  />
                                  <div className="position-absolute bottom-0 start-50 translate-middle-x mb-2 badge bg-white text-dark border border-gray-300 fs-7" style={{ zIndex: 1 }}>
                                    Building {tenant.houseNumber}
                                  </div>
                                </div>

                                <div className="d-flex flex-column justify-content-between flex-grow-1">
                                  <div>
                                    <h4 className="fw-bold text-gray-900 fs-4 mb-1">{tenant.typeName}</h4>
                                    <div className="text-gray-500 fs-7 mb-3">Assigned property in {tenant.phaseName}</div>
                                  </div>

                                  <div className="p-3 bg-light rounded-3 border border-gray-200 d-flex justify-content-between align-items-center">
                                    <div>
                                      <span className="text-gray-600 fs-8 d-block fw-bold">Basic Rent Assignment</span>
                                      <span className="fw-bold text-gray-900 fs-5">{tenant.basicRentFormatted}</span>
                                    </div>
                                    <button
                                      type="button"
                                      className="btn btn-sm btn-light-success fw-bold fs-8"
                                      onClick={() => setShowUpdateRentModal(true)}
                                    >
                                      Edit Rent
                                    </button>
                                  </div>
                                </div>
                              </div>

                              <div className="row g-3">
                                <div className="col-6">
                                  <div className={`p-4 rounded-3 h-100 ${
 dueMeta.badgeClass ==="badge-light-danger"
                                      ? "bg-light-danger border border-danger border-opacity-25"
                                      : dueMeta.badgeClass === "badge-light-warning"
                                      ? "bg-light-warning border border-warning border-opacity-25"
                                      : "bg-light-success border border-success border-opacity-25"
                                  }`}>
                                    <span className={`fs-7 fw-bold d-block mb-1 ${
 dueMeta.badgeClass ==="badge-light-danger"
                                        ? "text-danger"
                                        : dueMeta.badgeClass === "badge-light-warning"
                                        ? "text-warning"
                                        : "text-success"
                                    }`}>{dueMeta.badgeText}</span>
                                    <span className="text-gray-900 fw-bold fs-5">{dueMeta.days}</span>
                                  </div>
                                </div>

                                <div className="col-6">
                                  <div className="p-4 rounded-3 bg-light border border-gray-200 h-100">
                                    <span className="text-gray-600 fs-7 fw-bold d-block mb-1">Expiry Date</span>
                                    <span className="fw-bold fs-5 text-gray-900">{tenant.expiryLabel}</span>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Payer Info */}
                        <div className="col-12 col-xl-6">
                          <div className="card card-flush h-100 border-0 bg-white" style={{ borderRadius: "16px" }}>
                            <div className="card-header border-bottom border-gray-200 py-4 px-6">
                              <h3 className="card-title fw-bold text-gray-900 fs-5 mb-0 d-flex align-items-center gap-2">
                                <User className="w-4 h-4 text-primary" />
                                Payer & Tenant Information
                              </h3>
                              <span className={`badge ${tenant.isCorporate ?"badge-light-primary text-primary" : "badge-light-success text-success"} fw-bold fs-7`}>
                                {tenant.isCorporate ? "Corporate Tenant" : "Individual Tenant"}
                              </span>
                            </div>

                            <div className="card-body p-6">
                              <div className="d-flex align-items-center gap-5 mb-6">
                                <div className="symbol symbol-75px symbol-circle flex-shrink-0 border border-3 border-gray-200">
                                  {tenant.imageUrl ? (
                                    <img src={tenant.imageUrl} alt="avatar" className="object-fit-cover" />
                                  ) : (
                                    <span className="symbol-label bg-light-success text-success fw-bold fs-2">
                                      {tenant.initials}
                                    </span>
                                  )}
                                </div>

                                <div className="flex-grow-1">
                                  <h3 className="text-gray-900 fw-bold fs-3 mb-1">{tenant.name}</h3>
                                  <div className="text-gray-600 fs-7 fw-semibold d-flex align-items-center gap-2 mb-1">
                                    <Phone className="w-3.5 h-3.5 text-gray-500" />
                                    <span>{tenant.phone}</span>
                                  </div>
                                  <div className="text-gray-600 fs-7 fw-semibold d-flex align-items-center gap-2">
                                    <FileText className="w-3.5 h-3.5 text-gray-500" />
                                    <span>{tenant.email}</span>
                                  </div>
                                </div>
                              </div>

                              <div className="row g-3">
                                <div className="col-6">
                                  <div className="p-4 rounded-3 bg-light border border-gray-200 h-100">
                                    <span className="text-gray-600 fs-7 fw-bold d-block mb-1">Occupation</span>
                                    <span className="fw-bold fs-6 text-gray-900">{tenant.occupation}</span>
                                  </div>
                                </div>

                                <div className="col-6">
                                  <div className="p-4 rounded-3 bg-light border border-gray-200 h-100">
                                    <span className="text-gray-600 fs-7 fw-bold d-block mb-1">Assigned Unit</span>
                                    <span className="fw-bold fs-6 text-gray-900">{tenant.phaseName} - Unit {tenant.houseNumber}</span>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>

                      </div>

                      {/* Lower Section: Invoices & Payment Ledger Tables */}
                      <div className="card card-flush border-0 bg-white" style={{ borderRadius: "16px" }}>
                        <div className="card-header border-bottom border-gray-200 py-4 px-6 d-flex align-items-center justify-content-between flex-wrap gap-4">
                          <div className="d-flex gap-2 bg-light p-1 rounded-3 border border-gray-200">
                            <button
                              type="button"
                              onClick={() => setActiveTab("invoices")}
                              className={`btn btn-sm rounded-3 px-4 py-2 fs-7 fw-bold transition-all ${
 activeTab ==="invoices"
                                  ? "bg-white text-success border border-gray-200"
                                  : "text-gray-600 bg-transparent hover-text-gray-900"
                              }`}
                            >
                              <span>Rent Invoices ({invoices.length})</span>
                            </button>
                            <button
                              type="button"
                              onClick={() => setActiveTab("payments")}
                              className={`btn btn-sm rounded-3 px-4 py-2 fs-7 fw-bold transition-all ${
 activeTab ==="payments"
                                  ? "bg-white text-success border border-gray-200"
                                  : "text-gray-600 bg-transparent hover-text-gray-900"
                              }`}
                            >
                              <span>Payment History ({payments.length})</span>
                            </button>
                          </div>

                          <div className="position-relative" style={{ width: "240px" }}>
                            <Search className="w-4 h-4 text-gray-400 position-absolute top-50 start-0 translate-middle-y ms-3" />
                            <input
                              type="text"
                              className="form-control form-control-sm ps-9 bg-light border-0"
                              placeholder="Search history..."
                              value={searchQuery}
                              onChange={(e) => setSearchQuery(e.target.value)}
                            />
                          </div>
                        </div>

                        <div className="card-body p-6">
                          {activeTab === "invoices" ? (
                            filteredInvoices.length === 0 ? (
                              <div className="text-center py-15 text-muted">
                                <FileText className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                                <div className="fs-6 fw-bold text-gray-900 mb-1">No Rent Invoices Found</div>
                                <div className="fs-7 text-gray-500">There are no generated invoices for this property.</div>
                              </div>
                            ) : (
                              <div className="table-responsive">
                                <table className="table table-row-dashed align-middle gs-0 gy-4">
                                  <thead>
                                    <tr className="fw-bold text-gray-500 fs-7 text-uppercase border-bottom border-gray-200">
                                      <th>Invoice ID</th>
                                      <th>Billing Period</th>
                                      <th>Amount Due</th>
                                      <th>Due Date</th>
                                      <th>Status</th>
                                      <th>Component Breakdown</th>
                                      <th className="text-end">Action</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {filteredInvoices.map((inv, idx) => {
                                      const isPaid = (inv.status || "").toLowerCase() === "paid";
                                      const isOverdue = (inv.status || "").toLowerCase() === "overdue";

                                      return (
                                        <tr key={inv.id || idx}>
                                          <td>
                                            <div className="fw-bold text-gray-900 fs-7">{inv.id?.slice(0, 10) || `INV-${idx + 1}`}</div>
                                            <div className="fs-8 text-gray-500">{fmtDate(inv.issuedAt || inv.createdDate)}</div>
                                          </td>
                                          <td>
                                            <span className="fw-semibold text-gray-800 fs-7">
                                              {inv.periodName || inv.rentPeriod || "Annual Rent"}
                                            </span>
                                          </td>
                                          <td>
                                            <span className="fw-bold text-gray-900 fs-6">
                                              ₦{Number(inv.totalAmount || inv.rentAmount || 0).toLocaleString()}
                                            </span>
                                          </td>
                                          <td>
                                            <span className="fw-semibold text-gray-800 fs-7">
                                              {fmtDate(inv.dueDate)}
                                            </span>
                                          </td>
                                          <td>
                                            <span className={`badge ${
 isPaid ?"badge-light-success text-success" : isOverdue ? "badge-light-danger text-danger" : "badge-light-warning text-warning"
                                            } fw-bold fs-7`}>
                                              {inv.status || "Pending"}
                                            </span>
                                          </td>
                                          <td>
                                            {inv.components && inv.components.length > 0 ? (
                                              <div className="d-flex flex-wrap gap-2">
                                                {inv.components.map((comp, cIdx) => (
                                                  <span key={cIdx} className="badge badge-light text-gray-700 fs-8 border border-gray-200">
                                                    {comp.name || comp.type}: ₦{Number(comp.amount || 0).toLocaleString()}
                                                  </span>
                                                ))}
                                              </div>
                                            ) : (
                                              <span className="text-gray-400 fs-8">Basic Rent</span>
                                            )}
                                          </td>
                                          <td className="text-end">
                                            {!isPaid && (
                                              <button
                                                type="button"
                                                className="btn btn-sm btn-light-warning px-3 py-1 fs-8"
                                                onClick={handleSendReminder}
                                              >
                                                Remind
                                              </button>
                                            )}
                                          </td>
                                        </tr>
                                      );
                                    })}
                                  </tbody>
                                </table>
                              </div>
                            )
                          ) : (
                            filteredPayments.length === 0 ? (
                              <div className="text-center py-15 text-muted">
                                <CreditCard className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                                <div className="fs-6 fw-bold text-gray-900 mb-1">No Payment History Found</div>
                                <div className="fs-7 text-gray-500">There are no recorded payment transactions for this house.</div>
                              </div>
                            ) : (
                              <div className="table-responsive">
                                <table className="table table-row-dashed align-middle gs-0 gy-4">
                                  <thead>
                                    <tr className="fw-bold text-gray-500 fs-7 text-uppercase border-bottom border-gray-200">
                                      <th>Reference / Receipt</th>
                                      <th>Payment Date</th>
                                      <th>Amount Paid</th>
                                      <th>Payment Method</th>
                                      <th>Status</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {filteredPayments.map((pay, idx) => (
                                      <tr key={pay.id || idx}>
                                        <td>
                                          <div className="fw-bold text-gray-900 fs-7">{pay.reference || pay.id?.slice(0, 12) || `PAY-${idx + 1}`}</div>
                                          <div className="fs-8 text-gray-500">{pay.gateway || "PayU Gateway"}</div>
                                        </td>
                                        <td>
                                          <span className="fw-semibold text-gray-800 fs-7">{fmtDate(pay.paidAt || pay.createdDate)}</span>
                                        </td>
                                        <td>
                                          <span className="fw-bold text-success fs-6">
                                            ₦{Number(pay.amount || 0).toLocaleString()}
                                          </span>
                                        </td>
                                        <td>
                                          <span className="badge badge-light-primary text-primary fw-bold fs-7">
                                            {pay.paymentMethod || "Bank Transfer"}
                                          </span>
                                        </td>
                                        <td>
                                          <span className="badge badge-light-success text-success fw-bold fs-7">
                                            {pay.status || "Successful"}
                                          </span>
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            )
                          )}
                        </div>
                      </div>
                    </>
                  )}

                </div>
              </div>

              {/* Footer */}
              <div className="app-footer py-4 border-top bg-white">
                <div className="app-container container-fluid d-flex justify-content-center align-items-center">
                  <div className="text-gray-900 text-center fs-7">
                    <span className="text-muted fw-semibold me-1">2026&copy;</span>
                    <a href="https://pixels.com.ng/" target="_blank" rel="noreferrer" className="text-gray-800 text-hover-primary">
                      Powered by Pixels Solutions Limited
                    </a>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </div>
      </div>

      {/* Update Rent Modal */}
      {showUpdateRentModal && (
        <div className="modal fade show d-block" tabIndex={-1} style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
          <div className="modal-dialog modal-dialog-centered modal-lg">
            <div className="modal-content custom-card-modal border-0 overflow-hidden">
              <div className="modal-header bg-white border-bottom border-gray-200 px-6 py-4">
                <h3 className="fs-4 text-gray-900 fw-bold mb-0 d-flex align-items-center gap-2">
                  <DollarSign className="w-5 h-5 text-success" />
                  Update Rent Assignment
                </h3>
                <button
                  type="button"
                  className="btn btn-sm btn-icon btn-light btn-close-danger"
                  onClick={() => setShowUpdateRentModal(false)}
                >
                  <X className="w-4 h-4 text-gray-500" />
                </button>
              </div>

              <form onSubmit={handleUpdateRentSubmit}>
                <div className="modal-body p-6">
                  <div className="mb-5">
                    <label className="form-label fs-7 fw-bold text-gray-700">Basic Rent Amount (₦) <span className="text-danger">*</span></label>
                    <input
                      type="number"
                      min="0"
                      className="form-control form-control-solid fs-6"
                      placeholder="e.g. 10000000"
                      value={newRentAmount}
                      onChange={(e) => setNewRentAmount(e.target.value)}
                      required
                    />
                    <div className="fs-8 text-gray-500 mt-1">
                      Updating the rent creates a new active assignment for Building {tenant?.houseNumber} ({tenant?.typeName}).
                    </div>
                  </div>

                  <div className="mb-4">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <label className="form-label fs-7 fw-bold text-gray-700 m-0">Default Extra Charges (Optional)</label>
                      <button
                        type="button"
                        className="btn btn-sm btn-light-success d-flex align-items-center gap-1 fs-8"
                        onClick={() => setExtraCharges(prev => [...prev, { type: 1, name: "Service Charge", amount: "" }])}
                      >
                        <Plus className="w-3.5 h-3.5" />
                        Add Charge
                      </button>
                    </div>

                    {extraCharges.length === 0 ? (
                      <div className="text-center text-muted fs-8 py-4 border border-dashed rounded-3 bg-light">
                        No default extra charges added for this building.
                      </div>
                    ) : (
                      <div className="d-flex flex-column gap-3">
                        {extraCharges.map((comp, idx) => (
                          <div key={idx} className="row g-2 align-items-center">
                            <div className="col-4">
                              <select
                                className="form-select form-select-solid fs-7"
                                value={comp.type || 1}
                                onChange={(e) => {
                                  const val = Number(e.target.value);
                                  const defaultName = val === 1 ? "Service Charge" : "Other";
                                  setExtraCharges(prev => prev.map((c, i) => i === idx ? { ...c, type: val, name: c.name === "Service Charge" || c.name === "Other" || !c.name ? defaultName : c.name } : c));
                                }}
                              >
                                <option value={1}>Service Charge</option>
                                <option value={2}>Other</option>
                              </select>
                            </div>
                            <div className="col-4">
                              <input
                                type="text"
                                className="form-control form-control-solid fs-7"
                                placeholder="Name (e.g. Waste)"
                                value={comp.name || ""}
                                onChange={(e) => setExtraCharges(prev => prev.map((c, i) => i === idx ? { ...c, name: e.target.value } : c))}
                              />
                            </div>
                            <div className="col-3">
                              <input
                                type="number"
                                min="0"
                                className="form-control form-control-solid fs-7"
                                placeholder="Amount (₦)"
                                value={comp.amount || ""}
                                onChange={(e) => setExtraCharges(prev => prev.map((c, i) => i === idx ? { ...c, amount: e.target.value } : c))}
                              />
                            </div>
                            <div className="col-1 text-end">
                              <button
                                type="button"
                                className="btn btn-icon btn-sm btn-light-danger"
                                onClick={() => setExtraCharges(prev => prev.filter((_, i) => i !== idx))}
                              >
                                <Trash2 className="w-3.5 h-3.5 text-danger" />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                <div className="modal-footer bg-white border-top border-gray-200 px-6 py-4">
                  <button
                    type="button"
                    className="btn btn-light text-gray-700 btn-md fs-7 px-6"
                    onClick={() => setShowUpdateRentModal(false)}
                    disabled={updatingRent}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn btn-success btn-md fs-7 px-6 d-flex align-items-center gap-2"
                    disabled={updatingRent}
                  >
                    {updatingRent ? (
                      <><span className="spinner-border spinner-border-sm me-1"></span>Updating...</>
                    ) : (
                      <><Check className="w-3.5 h-3.5 text-white me-1" />Save Rent</>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
    </>
  );
}
